import {
  CaseItemType,
  QNAForm,
  ThreadItemType,
  ThreadMessagesData,
} from '../../interfaces/api/qna';
import {api, baseUrl} from '../api';
import http, {getToken, handleError} from '../http';
import {Platform} from 'react-native';
import {ChatType, Result} from '../../utils/enums';
import {HttpResponse} from '../../interfaces/api/http';
import {CategoryItem} from '../../interfaces/components/chatList';
import {convertUrlsToMarkdown} from '../../utils/markdownHelpers';
import {endSession} from '../../screens/auth/login/actions';
import {store} from '../../redux/store';
import {
  setMessageSuccess,
  setTypingStatus,
} from '../../screens/content/chat/reducer';

// Global request reference for canceling
let activeRequest: XMLHttpRequest | null = null;

/**
 * Stops the currently active SSE stream.
 * Call this function when you need to cancel an ongoing streaming request.
 */
export const stopStreamingSSE = () => {
  if (activeRequest) {
    activeRequest.abort();
    activeRequest = null;
    store.dispatch(setMessageSuccess());

    store.dispatch(
      setTypingStatus({status: {userId: 'genio', isTyping: false}}),
    );
  }
};

/**
 * API functions for question and answer functionality
 */
export const qna = {
  /**
   * Fetches all chat threads for the current user
   *
   * @param type - The type of chat threads to retrieve
   * @param search
   * @param tab_id - The tab ID to filter threads by
   * @returns Promise with the list of thread items
   */
  getUserThreads: (
    type: ChatType,
    search?: string,
    tab_id?: string,
  ): Promise<HttpResponse<ThreadItemType[]>> =>
    new Promise((resolve, reject) => {
      let url = `${api.qna.threads}?type=${type}`;
      if (search) {
        url += `&search=${search}`;
      }
      if (tab_id) {
        url += `&tab_id=${tab_id}`;
      }
      http
        .get<ThreadItemType[]>(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves messages for a specific thread
   *
   * @param threadId - The unique identifier of the thread
   * @returns Promise with thread messages data
   */
  getThreadMessages: (
    threadId: string | number,
  ): Promise<HttpResponse<ThreadMessagesData>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.threads}/${threadId}?`;
      http
        .get<ThreadMessagesData>(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves messages for a specific chat
   *
   * @param params - Contains chat ID and optional parameters
   * @returns Promise with chat messages data
   */
  getChatMessages: (params: {
    id: number;
    start?: number;
    length?: number;
    search?: string;
  }): Promise<HttpResponse<ThreadMessagesData>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.threads}/${params.id}?all=1&search=${params.search || ''}`;
      http
        .get<ThreadMessagesData>(url)
        .then(res => resolve(res as any))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves developer messages for a specific chat
   *
   * @param id - ID of the chat to get developer messages for
   * @returns Promise with developer messages data
   */
  getDevMessages: (id: number | string): Promise<HttpResponse<any>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.devMessages}/${id}`;
      http
        .get(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves developer message for debug modal
   * @param devMessageId - ID of the dev message to retrieve
   * @returns Promise with developer message data
   */
  getDevMessage: (devMessageId: number | string): Promise<HttpResponse<any>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.devMessages}/${devMessageId}`;
      http
        .get(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves available chat categories
   *
   * @returns Promise with categories data
   */
  getCategories: (tabId: string): Promise<HttpResponse<CategoryItem[]>> =>
    new Promise((resolve, reject) => {
      const url = `api/tabs/${tabId}/${api.qna.categories}`;
      http
        .get(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves categories for a specific tab
   * @param {number} tabId - The ID of the tab to retrieve categories for
   * @returns {Promise<any[]>} Promise resolving to an array of category items
   */
  getTabCategories: (tabId: number): Promise<any[]> =>
    new Promise((resolve, reject) => {
      const url = `api/tabs/${tabId}/categories`;
      http
        .get(url)
        .then(res => {
          const response = res as any;
          resolve(response?.content || []);
        })
        .catch(e => reject(e));
    }),

  /**
   * Retrieves all available tabs
   * @returns {Promise<any>} Promise resolving to the tabs response containing tab data
   */
  getAllTabs: (): Promise<any> =>
    new Promise((resolve, reject) => {
      const url = api.tabs?.getAll || 'api/tabs';
      http
        .get(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Sends a QNA request and streams the response using Server-Sent Events
   *
   * @param body - Form containing question data and thread information
   * @param setThreadId - Callback function to update thread ID in parent component
   * @param setMessage - Callback function to update message text in parent component
   * @returns Promise that resolves to a boolean indicating success or failure
   */
  sendStreamingQNA: (
    body: QNAForm,
    setThreadId: (id: string) => void,
    setMessage: (text: string, citations?: string[]) => void,
  ): Promise<boolean> =>
    new Promise(async (resolve, reject) => {
      try {
        if (!baseUrl) {
          throw new Error('Base URL is not defined');
        }

        // Create request payload
        const requestPayload = {
          question: body.question || '',
          ...(body.thread_id && {thread_id: body.thread_id}),
          ...(body.question_id && {category_id: body.question_id}),
        };

        // Build final URL - ensure no double slashes
        const cleanBaseUrl = baseUrl.endsWith('/')
          ? baseUrl.slice(0, -1)
          : baseUrl;
        const cleanApiPath = api.qna.ask.startsWith('/')
          ? api.qna.ask
          : '/' + api.qna.ask;
        const finalUrl = `${cleanBaseUrl}${cleanApiPath}`;

        // Set up headers for request
        const headers = {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
          'X-Device-OS': Platform.OS,
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        };

        // Stop any existing stream
        stopStreamingSSE();

        // Create new XMLHttpRequest
        const request = new XMLHttpRequest();
        activeRequest = request;

        let buffer = '';
        let accumulateMessage = '';
        let citations: string[] = [];
        let hasReceivedData = false;

        request.open('POST', finalUrl, true);
        request.timeout = 30000000;

        // Set headers
        Object.entries(headers).forEach(([key, value]) => {
          request.setRequestHeader(key, value);
        });

        // Handle state changes
        request.onreadystatechange = function () {
          if (this.readyState === 4) {
            if (this.status === 200) {
              resolve(true);
            } else if (this.status === 0 && !hasReceivedData) {
              reject(
                new Error(
                  'Connection failed. Please check your internet connection.',
                ),
              );
            } else if (this.status !== 0) {
              reject(new Error(`HTTP error! status: ${this.status}`));
            }
          }
        };

        // Handle streaming data
        request.onprogress = function () {
          try {
            // Get the new data
            const newData = this.responseText.substring(buffer.length);
            if (newData.length > 0) {
              hasReceivedData = true;
            }
            buffer = this.responseText;

            // Split response into lines
            const lines = newData.split('\n');

            for (const line of lines) {
              try {
                // Skip empty lines
                if (!line.trim()) {
                  continue;
                }

                const jsonData = JSON.parse(line);

                // Handle stream completion marker
                if (jsonData.text === '[DONE]') {
                  // Append citations at the end
                  setMessage(accumulateMessage, citations);
                  continue;
                }

                // Handle thread metadata
                if (jsonData.thread_id !== undefined) {
                  setThreadId(jsonData.thread_id.toString());
                  citations = jsonData.citations || [];

                  // If no thread_id in body, we assume this is a new thread
                  if (!body.thread_id) {
                    /* const newThread: ThreadItemType = {
                      id: jsonData.thread_id,
                      title: jsonData.title || body.question || '',
                      type: body.type,
                      created_at: new Date().toISOString(),
                      messages_count: 2,
                    };*/

                    // Add the new thread to the appropriate store
                    // if (
                    //   body.type &&
                    //   [ChatType.QNA, ChatType.Drugs].includes(body.type)
                    // ) {
                    //   addUserThreadChat(newThread);
                    // } else {
                    //   addUserThreadClinicalCase(newThread);
                    // }
                  }
                }
                // Handle text chunks
                else if (jsonData.text !== undefined) {
                  accumulateMessage += jsonData.text;
                  setMessage(accumulateMessage, citations);
                }

              } catch (error) {
                // Error processing line - continue with next
              }
            }
          } catch (error) {
            // Error in onprogress handler
          }
        };

        // Handle timeout
        request.ontimeout = function () {
          reject(new Error('Request timed out. Please try again.'));
        };

        // Handle errors
        request.onerror = function () {
          if (this.status === 401) {
            endSession();
          }
          if (!hasReceivedData) {
            reject(
              new Error(
                'Failed to connect to the server. Please check your internet connection.',
              ),
            );
          }
        };

        // Send the request with JSON payload
        request.send(JSON.stringify(requestPayload));
      } catch (error: any) {
        handleError(error);

        if (error?.response?.status === 401) {
          endSession();
        }
        reject(error);
      }
    }),

  /**
   * Retrieves available cases
   *
   * @returns Promise with cases data
   */
  getCases: (): Promise<HttpResponse<CaseItemType[]>> =>
    new Promise((resolve, reject) => {
      http
        .get<CaseItemType[]>(api.qna.cases)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),
};
